import { Note, NoteType, PostNoteAdd, PostSourceDataFindingSave, SourceDataFinding, SourceDataFindingStatus, SourceDataFindingType } from "@/api/dtos";
import { FC, useCallback, useContext, useMemo, useRef } from "react";
import InputOnlyField from "../../fields/inputOnly/InputOnlyField";
import Select from "../../inputs/Select";
import Text from "../../inputs/Text";
import noteTypeToText from "@/utilities/configuration/noteTypeToText";
import { AppSettings } from "@/AppSettings";
import Form from "../../Form";
import { Box, Button, Paper, styled } from "@mui/material";
import FormSubmitButton from "../../FormSubmitButton";
import { useLocation, useNavigate } from "react-router-dom";
import { FormError } from "@/errors";
import { Condition, ConditionMode, FormManager, IFormState, IFormSubmit, IFormSubmitFailed, IFormSubmitValidationFailed, IFormValidate, ValidateOn } from "@ngt/forms-core";
import FormErrorType from "@/types/form/FormErrorType";
import { useSnackbar } from "notistack";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import sourceDataFindingTypeToText from "@/utilities/configuration/sourceDataFindingTypeToText";
import DatePicker from "../../inputs/DatePicker";
import Checkbox from "../../inputs/Checkbox";
import asyncDebounce from "@/utilities/asyncDebounce";
import { DateTime } from "luxon";

const FormContainer = styled(Paper)(({ theme }) => ({ 
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
}));


const FormButtons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'

}));

const typeOptions = [
    {
        id: SourceDataFindingType.Information,
        name: sourceDataFindingTypeToText(SourceDataFindingType.Information)
    },
    {
        id: SourceDataFindingType.ProtocolDeviation,
        name: sourceDataFindingTypeToText(SourceDataFindingType.ProtocolDeviation)
    },
    {
        id: SourceDataFindingType.ImportantProtocolDeviation,
        name: sourceDataFindingTypeToText(SourceDataFindingType.ImportantProtocolDeviation)
    }
]

const actionRequiredOptions = [
    {
        id: 1,
        name: 'No',
        description: 'No additional actions are required.'
    },
    {
        id: 2,
        name: 'Yes',
        description: 'Additional actions are required.'
    }
]

const actionRequiredParse = (value?: number) => value === 2 ? SourceDataFindingStatus.Pending : SourceDataFindingStatus.NotApplicable;
const actionRequiredConvert = (value?: SourceDataFindingStatus) => value === SourceDataFindingStatus.Pending || value === SourceDataFindingStatus.Resolved ? 2 : 1;


const resolvedOptions = [
    {
        id: 1,
        name: 'No',
        description: 'The required additional actions have not been completed.'
    },
    {
        id: 2,
        name: 'Yes',
        description: 'The required additional actions have been completed.'
    }
]

const resolvedParse = (value?: number) => value === 2 ? SourceDataFindingStatus.Resolved : SourceDataFindingStatus.Pending;
const resolvedConvert = (value?: SourceDataFindingStatus) => value === SourceDataFindingStatus.Resolved ? 2 : 1;


export interface ISourceDataFindingFormProps {
    sourceDataFinding?: SourceDataFinding
}

const actionRequiredCondition = (state: IFormState<SourceDataFinding, FormError>) => {
    return !!state.value?.status && (state.value.status === SourceDataFindingStatus.Pending || state.value.status === SourceDataFindingStatus.Resolved);
}

const resolvedCondition = (state: IFormState<SourceDataFinding, FormError>) => {
    return !!state.value?.status && state.value.status === SourceDataFindingStatus.Resolved;
}

const onConditionNotMet = () => {};

const SourceDataFindingForm: FC<ISourceDataFindingFormProps> = ({
    sourceDataFinding
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const client = useContext(ServiceStackClientContext);
    
    const initialData = sourceDataFinding;

    const location = useLocation();
    const navigate = useNavigate();

    const returnUrl = useMemo(() => {
        if (sourceDataFinding?.id) {
            return location.pathname.replace(`/${sourceDataFinding.id}/resolve`, '');
        }

        return location.pathname.replace('/add', '');
    }, [location.pathname, sourceDataFinding]);

    
    const validate = useCallback<IFormValidate<SourceDataFinding, FormError>>(async (formState, formActions) => {
        let errors: Record<string, FormError[]> = {};

        if (!formState?.value?.type) {
            errors['type'] = [
                {
                    property: 'type',
                    message: 'Type must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        // if (!formState?.value?.reference) {
        //     errors['reference'] = [
        //         {
        //             property: 'reference',
        //             message: 'Reference must be provided!',
        //             type: FormErrorType.Normal
        //         }
        //     ];
        // }

        if (!formState?.value?.detail) {
            errors['detail'] = [
                {
                    property: 'detail',
                    message: 'Detail must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        if (!formState?.value?.createdDate) {
            errors['createdDate'] = [
                {
                    property: 'createdDate',
                    message: 'Date must be provided!',
                    type: FormErrorType.Normal
                }
            ];
        }

        if (formState?.value?.status === SourceDataFindingStatus.Resolved) {

            if (!formState?.value?.resolvedDate) {
                errors['resolvedDate'] = [
                    {
                        property: 'resolvedDate',
                        message: 'Date Resolved must be provided!',
                        type: FormErrorType.Normal
                    }
                ];
            }

            if (!formState?.value?.discussedWith) {
                errors['discussedWith'] = [
                    {
                        property: 'discussedWith',
                        message: 'Discussed With must be provided!',
                        type: FormErrorType.Normal
                    }
                ];
            }

            if (!formState?.value?.resolution) {
                errors['resolution'] = [
                    {
                        property: 'resolution',
                        message: 'Resolution must be provided!',
                        type: FormErrorType.Normal
                    }
                ];
            }
        }

        return errors;
    }, []);

    const debouncedValidate = useMemo(() => {
        return asyncDebounce(validate, 500);
    }, [validate]);

    const submit = useCallback<IFormSubmit<SourceDataFinding, FormError>>(async (formState, formActions) => {
        //await client.post(new PostNoteAdd({ ...formState.value }));

        await client.post(new PostSourceDataFindingSave({ data: new SourceDataFinding({...formState.value}) }))

        enqueueSnackbar(`Finding successfully saved!`, { variant: "success" });

        navigate(returnUrl);
        
        return {};
    }, [client, enqueueSnackbar, navigate, returnUrl]);

    const submitFailed = useCallback<IFormSubmitFailed<SourceDataFinding, FormError>>(async (formState, formActions) => {
        
        enqueueSnackbar(`An error occurred while attempting to save the finding!`, { variant: "error" });

        return {};
    }, [enqueueSnackbar]);

    const submitValidationFailed = useCallback<IFormSubmitValidationFailed<SourceDataFinding, FormError>>(async (formState, formActions, validationException) => {
        enqueueSnackbar("The form contains invalid data, please correct the marked fields and try again!", { variant: "warning" });

        return formState.errors;
    }, [enqueueSnackbar]);

    const formRef = useRef<FormManager<SourceDataFinding, FormError>>(null);
    
    const onResolved = useCallback((event: any, _: any, selectedItem: any) => {
        if (selectedItem.id === 2 && formRef.current) {
            formRef.current.setFieldValue('resolvedDate', DateTime.now().toISO({ includeOffset: false }), true, true, false, false);
        }
    }, [formRef]);

    const disableCreateFields = !!initialData?.id;

    return (
        <Form
            initialValue={initialData}
            formRef={formRef}
            debug={AppSettings.debug}
            onSubmit={submit}
            onSubmitFailed={submitFailed}
            onSubmitValidationFailed={submitValidationFailed}
            onValidate={debouncedValidate}
            validateOn={ValidateOn.onChange}
        >
            <FormContainer>   
                <InputOnlyField 
                    propertyPath="createdDate"
                    label="Date"
                    component={DatePicker}
                    slotProps={{
                        textField: {
                            margin: 'normal',
                            helperText: 'The date of the finding.'
                        }
                    }}
                    disabled={disableCreateFields}
                />        
                <InputOnlyField
                    propertyPath="type"
                    label="Type"
                    helperText="The type of finding."
                    FormControlProps={{
                        margin: 'normal'
                    }}
                    nullOption={false}
                    component={Select}
                    options={typeOptions}
                    disabled={disableCreateFields}
                />    
                <InputOnlyField
                    propertyPath="reference"
                    label="Reference"
                    helperText="A reference number or code used to uniquely identify the finding."
                    margin="normal"
                    component={Text}
                    disabled={disableCreateFields}
                />      
                <InputOnlyField
                    propertyPath="detail"
                    label="Detail"
                    helperText="The complete details of the note or query."
                    margin="normal"
                    component={Text}
                    multiline
                    minRows={2}
                    disabled={disableCreateFields}
                />                
                <InputOnlyField
                    propertyPath="status"
                    label="Action Required?"
                    helperText="Does the event require any additional actions to take place?"
                    FormControlProps={{
                        margin: 'normal'
                    }}
                    parse={actionRequiredParse as any}
                    convert={actionRequiredConvert as any}
                    nullOption={false}
                    component={Select}
                    options={actionRequiredOptions}
                    disabled={disableCreateFields}
                /> 
                <Condition 
                    mode={ConditionMode.Show}
                    subscription={{ value: true }}
                    condition={actionRequiredCondition}
                    onConditionNotMet={onConditionNotMet}
                >           
                    <InputOnlyField
                        propertyPath="status"
                        label="Resolved"
                        onChange={onResolved}
                        helperText="Have the required additional actions been completed?"
                        FormControlProps={{
                            margin: 'normal'
                        }}
                        parse={resolvedParse as any}
                        convert={resolvedConvert as any}
                        nullOption={false}
                        component={Select}
                        options={resolvedOptions}
                    /> 
                </Condition>
                <Condition 
                    mode={ConditionMode.Show}
                    subscription={{ value: true }}
                    condition={resolvedCondition}
                >
                    <InputOnlyField 
                        propertyPath="resolvedDate"
                        label="Date Resolved"
                        component={DatePicker}
                        slotProps={{
                            textField: {
                                margin: 'normal',
                                helperText: 'The date of the finding.'
                            }
                        }}
                    />
                    <InputOnlyField 
                        propertyPath="discussedWith"
                        label="Discussed With"
                        component={Text}
                        margin="normal"
                        helperText="Who was the finding discussed with to bring about the resolution?"
                    />     
                    <InputOnlyField
                        propertyPath="resolution"
                        label="Resolution"
                        helperText="How the finding was resolved."
                        margin="normal"
                        component={Text}
                        multiline
                        minRows={2}
                    /> 
                </Condition>
            </FormContainer>  
            <FormButtons>
                <FormSubmitButton
                    variant="contained"
                    color="primary"
                >
                    Save
                </FormSubmitButton>
                <Button
                    variant="contained"
                    color="inherit"
                    onClick={() => navigate(returnUrl)}
                >
                    Cancel
                </Button>
            </FormButtons>    
        </Form>
    )
}

export default SourceDataFindingForm;